/**
 * COB2B-762 / COB2B-787 / COB2B-788 — the single checkout gate for the cart page and
 * the mini-cart.
 *
 * Reads window.CeresB2BCreditData (snippets/credit-balance-data.liquid, rendered in
 * layout/theme.liquid) and resolves ONE verdict from three rules, in the priority
 * order both tickets specify:
 *
 *   1. account on hold   — company metafield, no network call
 *   2. credit limit      — COB2B-762's rule, customer path only
 *   3. minimum order val — read from the marker Liquid already sets
 *
 * Every failing rule is collected, in that order. Blocking is unchanged — any failing
 * rule disables all three checkout buttons — but what is SHOWN now depends on the
 * surface (COB2B-788 UAT):
 *
 *   - mini-cart and cart header — the highest-priority failing rule only, so an
 *     on-hold company is never told to add more to their cart.
 *   - the cart page's .cart__draft-validation — every failing rule, stacked in priority
 *     order, which is what the rep's Draft Order view has always done.
 *
 * That divergence is deliberate. CHECKOUT was collapsed to one message to
 * match the cart, and the stacked cart notice knowingly re-accepts the contradictory-pair case ("reduce your
 * cart" beside "add more items") on the one surface buyers actually work in. Do not
 * "fix" the inconsistency by extending stacking to the mini-cart — it is excluded on
 * purpose, because long stacked copy needs layout work there first.
 *
 * ── Why all three live in one module (COB2B-787 D1) ────────────────────────────
 * The three rules contend for the same three checkout buttons and the same two notice
 * elements. A second gate touching those ids is exactly the collision that lost
 * COB2B-709's mini-cart guard to COB2B-754. One module owns the contention.
 *
 * ── Why one fetch per page ─────────────────────────────────────────────────────
 * The open-orders total is a property of the COMPANY, not the cart: it does not
 * change when a line quantity does. Fetching it on every cart update would put a
 * Lambda round-trip on the critical path of every +/- click for no new information.
 * So: fetch once, hold it, and recompute locally against the live cart total.
 *
 * COB2B-788 UAT: the page no longer starts empty-handed. The backend mirrors
 * every successful lookup onto the company as custom.open_orders_total (COB2B-791),
 * so the payload carries the previous page's answer and the credit verdict applies at
 * first paint. The live lookup still runs and replaces it — the seed only fills what
 * used to be an up-to-8s window where credit abstained, the minimum order value
 * message showed first and a within-MOV cart could click through.
 *
 * ── Why state is re-applied rather than tracked ────────────────────────────────
 * Both surfaces rebuild by innerHTML/outerHTML on every cart change — assets/cart.js
 * swaps .normal-cart-header, assets/cart-drawer.js replaces .drawer__inner outright.
 * Anything this module sets is destroyed. Rather than fight that, we let Liquid
 * re-render the baseline and re-apply on top of it after each update. Nothing is
 * bound to an element inside .js-contents or .drawer__inner (COB2B-754 — listener
 * lifecycle is this area's standing source of bugs).
 */
import API_ENDPOINTS from '../config/api.js';
import { proxyFetch } from '../objects/proxyAuth.js';
import {
  isOnAccount,
  isAccountOnHold,
  dollarsToCents,
  cartTotalIncGstCents,
  evaluateCredit,
} from '../objects/cart/credit_rule.js';

// The tickets' final Figma copy, mirroring sections.cart.*_html in locales/en.default.json.
// Used when the locale key is absent — .shopifyignore blocks locales/*.json from CLI
// upload, so a key exists only once someone adds it in the customizer, and Liquid
// renders "Translation missing: ..." until then. Keep these two in step with the
// locale file: on most stores this is the copy that actually renders.
const ACCOUNTS_LINK = '<a href="mailto:AccRec@ceresorganics.com.au">Accounts</a>';
const FALLBACK_ON_HOLD_MESSAGE =
  'It looks like your account is on hold and you are unable to checkout. '
  + `Please contact ${ACCOUNTS_LINK} here for assistance.`;
// {creditLimit} is substituted with the money-formatted credit_limit metafield, already
// formatted by Liquid's `money` filter (credit-balance-data.liquid).
//
// COB2B-794 UAT-1: back to "Your Credit Limit of $X has been exceeded", now with the
// company's CREDIT LIMIT as $X — the client's wording, and what has been exceeded.
// This reverses the COB2B-788 UAT wording ("You have $X of available credit
// remaining"), which showed the remaining balance; the client asked for the limit.
// `credit_limit` is read for this sentence only — the rule never reads it.
const FALLBACK_CREDIT_MESSAGE =
  'Your Credit Limit of {creditLimit} has been exceeded. Please reduce your cart to '
  + `proceed or contact ${ACCOUNTS_LINK} here for assistance.`;
// A blank credit_limit has no figure to show, so the sentence drops it (agreed for
// COB2B-794 UAT-1) rather than reading "of  has been exceeded" or "of $0.00".
const FALLBACK_CREDIT_MESSAGE_NO_FIGURE =
  'Your Credit Limit has been exceeded. Please reduce your cart to '
  + `proceed or contact ${ACCOUNTS_LINK} here for assistance.`;

// Fail-closed means a hung request blocks checkout indefinitely, which reads as a
// broken page rather than a credit decision. Give up early and block deliberately.
const LOOKUP_TIMEOUT_MS = 8000;

const CHECKOUT_CTA_IDS = [
  'cart-checkout-now', // snippets/normal-cart-header.liquid — inside .normal-cart-header, re-rendered
  'checkout', // snippets/draft-order-cart-summary.liquid — NOT re-rendered, survives updates
  'CartDrawer-Checkout', // snippets/miniCart-footer.liquid — inside .drawer__inner, re-rendered
];

/**
 * Where a verdict's message is written.
 *
 * Selector-based rather than id-based since COB2B-788, to reach `.cart__draft-validation`
 * — the sticky Order Details notice on the cart page. That element is rendered for
 * everyone (draft-order-cart-summary.liquid), and before COB2B-788 nothing here ever
 * wrote to it, so Liquid's minimum-order-value copy could sit there permanently while
 * an on-hold or credit verdict was shown on the other two surfaces: two messages on
 * screen at once, which is exactly what the priority order exists to prevent.
 *
 * `visibility` differs because the elements do: the two error blocks use the `hidden`
 * property, while `.cart__draft-validation` is shown and hidden through an inline
 * `style="display: block|none"` that Liquid writes and draft_cart.js maintains.
 *
 * `customerOnly` keeps the rep path out of it. On a rep's cart page draft_cart.js owns
 * this element outright — it rebuilds its contents on every validation pass with the
 * rep's own shorter copy — so writing here too would have the two modules fighting over
 * one element (COB2B-787).
 *
 * `stacked` is the one surface that lists every failing rule instead of the top one
 * (COB2B-788 UAT). Declared here rather than branched in the render so the
 * divergence from the other two surfaces is readable in one place.
 */
const NOTICE_TARGETS = [
  { selector: '#cart-errors', visibility: 'hidden', isHeader: true },
  { selector: '#CartDrawer-CartErrors', visibility: 'hidden' },
  {
    selector: '.cart__draft-validation',
    visibility: 'display',
    customerOnly: true,
    stacked: true,
  },
];

// One rule per line, no bullet — the same separator draft_cart.js uses for the rep's
// list in this very element, so the two audiences read identically.
const STACK_SEPARATOR = '<br>';

// Marks the message as ours so assets/cart.js's setCartErrors() leaves it alone,
// exactly as it already does for data-minimum-order-value. One marker per rule so a
// verdict change swaps cleanly, and so the guard list in setCartErrors names the rule
// it is standing down for. A stacked notice can legitimately carry more than one at a
// time, so these are handled as a set — see clearOwnedMarkers.
const CREDIT_MARKER = 'data-credit-limit-exceeded';
const ON_HOLD_MARKER = 'data-account-on-hold';
const OWNED_MARKERS = [ON_HOLD_MARKER, CREDIT_MARKER];

// Liquid's own marker. Read, never written: the shortfall is computed in Liquid on all
// three surfaces and re-deriving it here would make a fourth implementation of the
// minimum order value — the documented cause of the divergence in this area
// (COB2B-788 D2). The button state and the figure on screen must come from one source.
const MOV_MARKER = 'data-minimum-order-value';
const MOV_SOURCES = ['#cart-errors', '#CartDrawer-CartErrors', '.cart__draft-validation'];

// Remembers what Liquid rendered before we touched a button, so clearing the block
// restores the real baseline instead of blindly enabling. Needed for #checkout, which
// no re-render ever replaces.
const BASE_DISABLED = 'data-credit-base-disabled';

/**
 * What a notice element said before this module took it over.
 *
 * A WeakMap rather than a data-attribute because every render replaces the element
 * outright, so the entry dies with the element it described and there is nothing to
 * clean up. Needed because the element we overwrite may be carrying Liquid's minimum
 * order value message: blanking it on release — which is what COB2B-762 did, when
 * credit was the only rule that could own it — would delete a message that is still
 * true and that Liquid will not re-render until the next cart change.
 */
const noticeBaseline = new WeakMap();

function resolveMessage(raw, fallback) {
  const text = raw || '';
  if (!text || text.indexOf('Translation missing') !== -1) return fallback;
  return text;
}

/**
 * The credit message names the company's credit limit (COB2B-794 UAT-1). Liquid already
 * interpolated it on the locale path, so the replace below is a no-op there; the
 * fallback carries a {creditLimit} placeholder this fills instead.
 *
 * With no credit limit, Liquid emits no blockedMessage at all (the store's locale
 * sentence has a slot for the figure), so the no-figure fallback is what renders.
 *
 * The same sentence also covers the fail-closed blocks (lookup failed, no balance) —
 * agreed for COB2B-794 UAT-1, as before when they shared the remaining-balance copy.
 */
function resolveCreditMessage(payload) {
  const creditLimit = payload.creditLimitMoney;
  const fallback = creditLimit
    ? FALLBACK_CREDIT_MESSAGE
    : FALLBACK_CREDIT_MESSAGE_NO_FIGURE;
  const message = resolveMessage(payload.blockedMessage, fallback);
  return message.replace('{creditLimit}', creditLimit || '');
}

/** The header keeps its copy in a child span; the drawer's error element has none. */
function noticeTextEl(el) {
  return el.querySelector('.normal-cart-header--notice-text') || el;
}

function ownedMarkers(el) {
  return OWNED_MARKERS.filter((marker) => el.hasAttribute(marker));
}

/**
 * Drop every marker this module may have set, not just the first one found.
 *
 * A stacked notice can carry on-hold AND credit at once, and a marker left behind keeps
 * assets/cart.js's setCartErrors() stood down for that element permanently — so a later
 * cart-update failure would have nowhere to report itself.
 */
function clearOwnedMarkers(el) {
  OWNED_MARKERS.forEach((marker) => el.removeAttribute(marker));
}

/** True while Liquid says the cart is under the company's minimum order value. */
function belowMinimumOrderValue() {
  return MOV_SOURCES.some((selector) => {
    const el = document.querySelector(selector);
    return !!el && el.hasAttribute(MOV_MARKER);
  });
}

/**
 * True on a page that carries a stacked notice — in practice the cart page.
 *
 * Only that surface shows a credit verdict alongside an on-hold one, so only that page
 * needs the open-orders lookup for an on-hold company. See creditNeeded.
 */
function hasStackedNotice() {
  return NOTICE_TARGETS.some(
    (target) => target.stacked && document.querySelector(target.selector),
  );
}

function applyToButton(id, blocked) {
  const el = document.getElementById(id);
  if (!el) return;

  if (!el.hasAttribute(BASE_DISABLED)) {
    el.setAttribute(BASE_DISABLED, el.disabled ? 'true' : 'false');
  }
  const base = el.getAttribute(BASE_DISABLED) === 'true';

  // Never enable something Liquid disabled for its own reasons — an empty cart, or
  // the minimum order value rule on #cart-checkout-now.
  el.disabled = base || blocked;
}

/**
 * The rules this surface writes itself: the top one, or all of them where the target
 * stacks. The minimum order value rule carries no message of its own, so it is absent
 * here by construction — Liquid owns that sentence (see stackedHtml).
 */
function writtenRules(target, verdict) {
  if (target.stacked) return verdict.rules.filter((rule) => rule.message);
  const top = verdict.rules[0];
  return top && top.message ? [top] : [];
}

/**
 * The stacked notice, in priority order.
 *
 * The minimum order value line is taken from the captured baseline — that is Liquid's
 * own sentence, already in this element before anything was written over it — rather
 * than re-derived. Re-deriving the shortfall here would be a fourth implementation of
 * the rule and the documented cause of divergence in this area (see MOV_MARKER).
 */
function stackedHtml(verdict, rules, baseline) {
  const parts = rules.map((rule) => rule.message);
  const liquidText = ((baseline && baseline.html) || '').trim();
  const movFailing = verdict.rules.some((rule) => rule.liquidOwned);
  if (movFailing && liquidText) parts.push(liquidText);
  return parts.join(STACK_SEPARATOR);
}

/**
 * Show the verdict, or hand the element back to whatever Liquid rendered.
 *
 * A blocking verdict this surface has no message for (minimum order value alone)
 * deliberately falls into the release branch: Liquid has already written that copy into
 * this exact element, so the only thing left to do is stop covering it.
 */
function applyToNotice(verdict, isSalesRep) {
  NOTICE_TARGETS.forEach((target) => {
    if (target.customerOnly && isSalesRep) return;

    const el = document.querySelector(target.selector);
    if (!el) return;

    const byDisplay = target.visibility === 'display';
    const owned = ownedMarkers(el);
    const rules = writtenRules(target, verdict);

    if (rules.length) {
      // Captured before anything is written over it, because on the stacked surface
      // this text IS the minimum order value line that stackedHtml reads back.
      if (!noticeBaseline.has(el)) {
        noticeBaseline.set(el, {
          html: noticeTextEl(el).innerHTML,
          hidden: el.hidden,
          display: el.style.display,
        });
      }
      const html = target.stacked
        ? stackedHtml(verdict, rules, noticeBaseline.get(el))
        : rules[0].message;

      clearOwnedMarkers(el);
      // innerHTML, not textContent: the messages carry the accounts mailto link. The
      // source is a locale string or the constants above — never anything user-entered.
      noticeTextEl(el).innerHTML = html;
      if (byDisplay) el.style.display = 'block';
      else el.hidden = false;
      rules.forEach((rule) => el.setAttribute(rule.marker, ''));
      if (target.isHeader) {
        document.querySelector('.normal-cart-header--actions')?.classList.add('with-error-message');
      }
      return;
    }

    // Only release what we took. A freshly re-rendered element carries Liquid's own
    // message and no marker, so it is left exactly as rendered.
    if (!owned.length) return;

    clearOwnedMarkers(el);
    const baseline = noticeBaseline.get(el);
    noticeTextEl(el).innerHTML = baseline ? baseline.html : '';
    if (byDisplay) el.style.display = baseline ? baseline.display : 'none';
    else el.hidden = baseline ? baseline.hidden : true;
    noticeBaseline.delete(el);
    if (target.isHeader) {
      document
        .querySelector('.normal-cart-header--actions')
        ?.classList.toggle('with-error-message', !el.hidden);
    }
  });
}

/**
 * True when this looks like the Ajax cart JSON the GST-inclusive total is worked out
 * from (/cart.js, /cart/change.js, or the payload's first-paint copy).
 */
function isCartJson(cart) {
  return !!cart && typeof cart.total_price === 'number' && Array.isArray(cart.items);
}

/**
 * Live cart from /cart.js. Mirrors miniCart.js's read on the same event.
 *
 * COB2B-794 UAT-1: returns the whole cart, not just total_price — the GST-inclusive
 * total needs each line's taxability and price (cartTotalIncGstCents).
 */
function fetchCart() {
  return fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((res) => (res.ok ? res.json() : null))
    .then((cart) => (isCartJson(cart) ? cart : null))
    .catch(() => null);
}

function fetchOpenOrdersTotal(companyId) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);

  return proxyFetch(API_ENDPOINTS.COMPANY_OPEN_ORDERS, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ company_id: companyId }),
    signal: controller.signal,
  })
    .then((res) => {
      if (!res.ok) throw new Error(`company-open-orders HTTP ${res.status}`);
      return res.json();
    })
    .then((body) => dollarsToCents(body?.data?.openOrdersTotal))
    .catch((err) => {
      if (window.console && console.warn) {
        console.warn('[CeresB2B] credit check could not read open orders:', err);
      }
      return null; // → lookupFailed → blocked
    })
    .finally(() => clearTimeout(timer));
}

const creditBalanceGate = () => {
  const payload = window.CeresB2BCreditData;
  // No payload means DTC or anonymous — no company, so none of the three rules apply.
  if (!payload || !payload.companyId) return;

  const onHold = isAccountOnHold(payload.accountStatus);
  const onHoldMessage = resolveMessage(payload.onHoldMessage, FALLBACK_ON_HOLD_MESSAGE);
  const creditMessage = resolveCreditMessage(payload);

  // COB2B-788: the credit rule now applies to both audiences. It used to exclude sales
  // reps (COB2B-762 scoped it to customers), but 787's sales-rep item 4 asks for the
  // rep's mini-cart to carry it too, and this gate already owns #CartDrawer-Checkout on
  // every page — the one surface where a rep needs it. The rep's Draft Order page is
  // still not this module's business: draft_cart.js owns those buttons and now shares
  // the same credit_rule.js evaluation.
  //
  // Relevance and need are two different questions, and folding them into one flag is
  // what made the rep's Draft Order page silently drop its credit-limit error whenever
  // the account was on hold — the figures had not changed, the lookup simply never ran:
  //
  //   applies — is this company subject to the credit rule at all? On-account only.
  //             This is what gets published to draft_cart.js.
  //   needed  — does anything on THIS page want the verdict? Two surfaces do even when
  //             the account is on hold, because both list every failing rule rather than
  //             only the top one: the rep's Draft Order page, and — since the COB2B-788
  //             UAT — the customer cart page's stacked notice. Anywhere else on
  //             hold outranks credit and nothing would render it, so the lookup stays
  //             skipped rather than spending a Lambda round-trip on an invisible answer.
  const creditApplies = isOnAccount(payload.paymentTerms);
  const creditNeeded =
    creditApplies && (!onHold || payload.isSalesRep || hasStackedNotice());

  const remainingBalanceCents = dollarsToCents(payload.remainingBalance);

  let openOrdersTotalCents = null;
  let lookupFailed = true;
  // Undecided until the lookup below answers. Until then the credit rule abstains
  // rather than blocking: it is the same "nothing applied yet" window COB2B-762 had,
  // now made explicit because on hold and minimum order value apply immediately and
  // must not wait on a network call to do so.
  let creditDecided = false;
  // COB2B-794 UAT-1: the cart itself, not just its price, so the rule can compare the
  // GST-inclusive Total the customer sees. The payload carries the same Ajax shape for
  // first paint (credit-balance-data.liquid).
  let cart = isCartJson(payload.cart) ? payload.cart : null;
  // Rapid +/- clicks fire overlapping /cart.js reads. Without a sequence guard a
  // slow early response can land last and re-evaluate against a stale, smaller cart
  // — which would wrongly UNBLOCK. Only the newest request may apply its result.
  let cartReadToken = 0;

  /**
   * Every failing rule, in priority order. `blocked` is the same answer it has always
   * been — any failing rule disables all three checkout buttons — and each surface
   * decides how much of `rules` it shows (see NOTICE_TARGETS).
   *
   * On hold no longer returns early. It never short-circuited TO allowed, so nothing
   * below it was fail-open before and nothing is now; what changes is that an on-hold
   * company's credit verdict is evaluated rather than skipped, which the stacked
   * surface needs (COB2B-788 UAT).
   */
  const resolveVerdict = () => {
    const rules = [];

    if (onHold) {
      rules.push({ marker: ON_HOLD_MARKER, message: onHoldMessage });
    }

    if (creditApplies && creditDecided) {
      const credit = evaluateCredit({
        remainingBalanceCents,
        openOrdersTotalCents,
        cartTotalCents: cartTotalIncGstCents(
          cart,
          payload.currencyIsoCode,
          payload.taxesIncluded,
        ),
        lookupFailed,
      });
      if (credit.blocked) {
        rules.push({ marker: CREDIT_MARKER, message: creditMessage });
      }
    }

    // Blocks, but writes nothing of its own: Liquid owns this sentence on all three
    // surfaces. `liquidOwned` is how the stacked surface finds it again — the text is
    // read back from the captured baseline rather than re-derived here.
    if (belowMinimumOrderValue()) {
      rules.push({ marker: null, message: null, liquidOwned: true });
    }

    return { blocked: rules.length > 0, rules };
  };

  const evaluate = () => {
    const verdict = resolveVerdict();
    CHECKOUT_CTA_IDS.forEach((id) => applyToButton(id, verdict.blocked));
    applyToNotice(verdict, payload.isSalesRep);
    return verdict;
  };

  /**
   * Publish the credit inputs for draft_cart.js (COB2B-788).
   *
   * The rep's Draft Order page runs its own validation on its own buttons, but must
   * evaluate the SAME credit rule against the SAME figures. The open-orders total is a
   * property of the company, not the cart, so COB2B-762 fetches it once per page — two
   * modules fetching it separately on the same page would undo that.
   *
   * One-way and read-only by contract: this module never reads anything draft_cart.js
   * writes. `decided` is the important field — until the lookup resolves, the consumer
   * must abstain rather than assume either outcome (see draft_cart.js).
   *
   * `applies` answers only "is this company subject to the credit rule" (on-account).
   * It deliberately does NOT carry this module's own on-hold suppression: that is a
   * property of how the CART displays one message at a time, not of whether the company
   * is over its limit, and exporting it made the rep's Draft Order page — which shows
   * every failing rule — hide a real credit block behind an on-hold one.
   *
   * `{ applies: true, decided: false }` is therefore a real, stable state: the customer
   * path of an on-hold company, where the rule is relevant but was never evaluated
   * because nothing on that page would show it. Consumers must treat it as "no answer",
   * which is exactly what abstaining on `decided` already does.
   */
  const publishCreditInputs = () => {
    payload.creditInputs = {
      applies: creditApplies,
      decided: creditDecided,
      openOrdersTotalCents,
      lookupFailed,
    };
    $(window).trigger('ceres:credit-inputs-updated');
  };

  // Re-apply after every cart change: the markup we just modified has been replaced
  // by Liquid, taking our disabled state and message with it. The company figures are
  // unchanged, so only the cart total is re-read.
  //
  // Registered BEFORE the balance check below — a blocked company must stay blocked
  // across cart updates, and skipping this for the no-balance case would let the next
  // quantity change silently restore an enabled checkout button.
  //
  // assets/cart-drawer.js's renderContents() fires this event too (COB2B-787 D4), so
  // an add-to-cart from a product page re-applies the verdict instead of losing it.
  //
  // COB2B-788 UAT: assets/cart.js's updateQuantity() now fires this AFTER the new
  // markup is in and passes the cart total /cart/change already returned. With that,
  // one evaluate against the new markup and the new total is the whole job — no second
  // round-trip during which the fresh render shows an enabled button and no credit
  // message. Callers without a total (add-to-cart, a rejected line, the company-switch
  // clear) keep the evaluate-then-read path below.
  //
  // COB2B-794 UAT-1: the event now carries the whole /cart/change state as `cart`
  // (its line items are needed for GST); `cartTotal` rides along for anything that only
  // wants the number.
  $(window).on('cart:quantity-update', function (event, detail) {
    if (detail && isCartJson(detail.cart)) {
      cartReadToken += 1; // supersede any /cart.js read still in flight
      cart = detail.cart;
      evaluate();
      return;
    }

    // Twice, deliberately. On hold and the minimum order value marker are both
    // readable from the markup that was just rendered, so re-apply them now rather
    // than leaving a live checkout button on screen for the length of a /cart.js
    // round-trip — that window is exactly what the drawer's add-to-cart path shows
    // the customer. Only the credit rule needs the new total, so it waits.
    evaluate();

    cartReadToken += 1;
    const token = cartReadToken;
    fetchCart().then((fresh) => {
      if (token !== cartReadToken) return; // superseded by a newer change
      if (fresh !== null) cart = fresh;
      evaluate();
    });
  });

  // COB2B-788 UAT — start decided from the last known open-orders total when
  // there is one (see the file header). Only when a balance exists: a missing balance
  // decides the outcome on its own below, and that fail-closed branch must not be
  // pre-empted. The live lookup further down still runs and overwrites all three; a
  // failed live lookup still blocks, exactly as before.
  const seededOpenOrdersCents = dollarsToCents(payload.openOrdersTotal);
  if (creditNeeded && remainingBalanceCents !== null && seededOpenOrdersCents !== null) {
    openOrdersTotalCents = seededOpenOrdersCents;
    lookupFailed = false;
    creditDecided = true;
  }

  // On hold and minimum order value are both decided from what is already on the page,
  // so apply them now rather than behind the lookup a prepay or on-hold company never
  // makes. With a seed, credit is applied here too — and published, so the rep's Draft
  // Order page (draft_cart.js) stops abstaining at the same moment.
  evaluate();
  publishCreditInputs();

  if (!creditNeeded) return;

  // A missing balance on an on-account company already decides the outcome — block,
  // and do not spend a backend call establishing exposure that cannot change it.
  if (remainingBalanceCents === null) {
    lookupFailed = false; // nothing was attempted; the block is "no balance", not "lookup failed"
    creditDecided = true;
    evaluate();
    publishCreditInputs();
    return;
  }

  fetchOpenOrdersTotal(payload.companyId).then((total) => {
    openOrdersTotalCents = total;
    lookupFailed = total === null;
    creditDecided = true;
    evaluate();
    publishCreditInputs();
  });
};

export default creditBalanceGate;
